import React, { useEffect, useState } from 'react';
import Big from 'big.js';
import Highlight from 'react-highlight';
import format from 'xml-formatter';
import ReactModal from 'react-modal';
import moment from 'moment';

import { ConfirmModal } from '../../common';
import { kbStatus, kbTypes } from '../../../helpers/data-constants';
import { kbService } from '../../../services/kb-exchange-service';
import { dealService } from '../../../services/deals-service';
import { messages } from '../../../helpers/messages';
import { userService } from '../../../services/user-service';
import Loader from './Loader';

import './kb-details.scss';
import 'highlight.js/styles/default.css';

import CrossIcon from '../../../assets/icons/cross.svg';

const KBMessageDetails = ({ messageId, dealId, onReload }) => {
  const [message, setMessage] = useState({});
  const [deal, setDeal] = useState({});
  const [user, setUser] = useState({});
  const [xmlModal, setXmlModal] = useState(false);
  const [processedModal, setProcessedModal] = useState(false);
  const [resendModal, setResendModal] = useState(false);
  const [loadingDeal, setLoadingDeal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);

  const errorHandler = (error) => {
    if (error.response) {
      let errorData = error.response.data;
      let commonError = messages[0];
      if (errorData.errorCode) {
        commonError = messages[errorData.errorCode];
      }
      window.scrollTo(0, 0);
      throw commonError;
    }
  };

  useEffect(() => {
    if (dealId !== -1) {
      setLoadingDeal(true);
      dealService
        .getContract(dealId)
        .then(({ data }) => setDeal(data))
        .catch(errorHandler)
        .finally(() => {
          setLoadingDeal(false);
        });
    }
  }, [dealId]);

  useEffect(() => {
    setLoadingMessage(true);
    kbService
      .getMessage(messageId)
      .then(({ data }) => {
        setMessage(data.details);
        if (data.details.userId && data.details.userId !== -1) {
          userService
            .getUser(data.details.userId)
            .then(({ data }) => setUser(data))
            .catch(errorHandler)
            .finally(() => {
              setLoadingMessage(false);
            });
        } else {
          setLoadingMessage(false);
        }
      })
      .catch((err) => {
        errorHandler(err);
        setLoadingMessage(false);
      });
  }, [messageId]);

  const getForamattedXml = (base64) => {
    let xml = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    return format(xml, {
      indentation: '  ',
      filter: (node) => node.type !== 'Comment',
      collapseContent: true,
      lineSeparator: '\n',
    });
  };

  const markOperation = () => {
    setLoadingMessage(true);
    kbService
      .markMessage(messageId, !message.processed)
      .then(({ data }) => {
        setMessage(data.details);
      })
      .catch(errorHandler)
      .finally(() => {
        setLoadingMessage(false);
        setProcessedModal(false);
        onReload();
      });
  };

  const resendOperation = () => {
    setLoadingMessage(true);
    kbService
      .createMessage({
        type: message.event,
        dealId: message.dealId,
        clientId: message.clientId,
        repeat: message.id,
      })
      .then(({ data }) => {})
      .catch(errorHandler)
      .finally(() => {
        setLoadingMessage(false);
        setResendModal(false);
        onReload();
      });
  };

  return (
    <td colSpan="9">
      {loadingDeal || loadingMessage ? (
        <Loader />
      ) : (
        <div className="exchange-details-content-section">
          <div className="exchange-details-content-section-left">
            {deal && deal.dealId && (
              <div className="exchange-details-content-card">
                <div className="exchange-details-content-card-group">
                  <div className="exchange-details-content-card-group-item">
                    <h3 className="exchange-details-content-card-group-item-name">
                      <strong>
                        <big>{deal?.cashBox && deal.cashBox}</big>
                      </strong>
                    </h3>
                  </div>
                  <div className="exchange-details-content-card-group-item">
                    <p>{deal?.basePercent && deal.basePercent.toFixed(2)}% / день</p>
                  </div>
                </div>
                <div className="exchange-details-content-card-group">
                  <div className="exchange-details-content-card-group-item">
                    <p>
                      <strong>
                        <big>{deal?.refundAmount && deal.refundAmount.toFixed(2)} руб.</big>
                      </strong>
                    </p>
                  </div>
                </div>
                <div className="exchange-details-content-card-group">
                  <div className="exchange-details-content-card-group-item">
                    <p className="exchange-details-date">
                      {deal?.startDate && moment(deal.startDate).format('DD.MM.YYYY')} -{' '}
                      {deal?.endDate && moment(deal.endDate).format('DD.MM.YYYY')}
                    </p>
                  </div>
                  <div className="exchange-details-content-card-group-item">
                    <p>
                      Пеня за просрочку{' '}
                      {new Big(deal.basePercent).plus(deal.basePercent).toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="exchange-details-content-section-right">
            <table className="table hover exchange-details-table">
              <thead>
                <tr>
                  <th scope="col">
                    <p>Дата</p>
                  </th>
                  <th scope="col">
                    <p>Статус</p>
                  </th>
                  <th scope="col">
                    <p>Описание</p>
                  </th>
                  <th scope="col">
                    <p>Оператор</p>
                  </th>
                  <th scope="col" />
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {message && message?.id && (
                  <React.Fragment key={message.id}>
                    <tr>
                      <td>
                        <p>{moment(message.time).format('DD.MM.YYYY, HH:mm:ss')}</p>
                      </td>
                      <td style={{ minWidth: '115px' }}>
                        <p>
                          <span className={'label ' + kbStatus[message.status].label}>
                            {kbStatus[message.status].text}
                          </span>
                        </p>
                      </td>
                      <td>
                        <p>{kbTypes[message.event || 'UNKNOWN'] || kbTypes['UNKNOWN']}</p>
                      </td>
                      <td>
                        <p>
                          {message.userId &&
                            user.lastName &&
                            user.lastName +
                              (user.firstName ? ' ' + user.firstName + ' ' : '') +
                              (user.middleName ? ' ' + user.middleName + ' ' : '')}
                        </p>
                      </td>
                      <td rowSpan={message.status === 'ERROR' ? 2 : 1}>
                        <div className="table-cell-actions-center">
                          <button
                            className="button-icontrigger bordered"
                            onClick={() => {
                              setXmlModal(true);
                            }}>
                            <span>Просмотр</span>
                          </button>

                          <ReactModal
                            isOpen={xmlModal}
                            contentLabel="Modal"
                            className="modal-container exchange-modal"
                            overlayClassName="modal-overlay">
                            <div className="modal-header">
                              <h2>Просмотр содержания сообщения </h2>
                              <button
                                className="button-icontrigger modal-closetrigger"
                                onClick={() => {
                                  setXmlModal(false);
                                }}>
                                <span className="icon">
                                  <CrossIcon />
                                </span>
                              </button>
                            </div>
                            <div className="modal-body">
                              <Highlight className="language-xml">
                                {getForamattedXml(message.xml)}
                              </Highlight>
                            </div>
                          </ReactModal>
                        </div>
                        <div className="table-cell-actions-center">
                          <button
                            className="button-icontrigger bordered"
                            onClick={() => {
                              setProcessedModal(true);
                            }}>
                            <span>{message.processed ? 'Вернуть' : 'Обработано'}</span>
                          </button>
                          <ConfirmModal
                            isOpen={processedModal}
                            handleClose={() => {
                              setProcessedModal(false);
                            }}
                            handleAction={markOperation}
                            headerText="Обработка"
                            bodyText={
                              message.processed
                                ? 'Вернуть операцию в обработку?'
                                : 'Пометить операцию как обработанную?'
                            }
                          />
                        </div>
                        <div className="table-cell-actions-center">
                          <button
                            className="button-icontrigger bordered"
                            onClick={() => {
                              setResendModal(true);
                            }}>
                            <span>Повтор</span>
                          </button>
                          <ConfirmModal
                            isOpen={resendModal}
                            handleClose={() => {
                              setResendModal(false);
                            }}
                            handleAction={resendOperation}
                            headerText="Повтор"
                            bodyText={'Повторить операцию?'}
                          />
                        </div>
                      </td>
                    </tr>
                    {message.status === 'ERROR' && (
                      <tr>
                        <td>
                          <p>{message.responseCode}</p>
                        </td>
                        <td colSpan={'3'}>
                          <p>{message.responseMessage}</p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </td>
  );
};

export default KBMessageDetails;
